/**
 * EndpointSkillUsagePanel — per-endpoint skill inventory (O-769), mounted in
 * the endpoint detail drawer. Lists every skill the agent has observed on this
 * endpoint with its disk path, SKILL.md preview, and invocation counts.
 *
 * Admin-only (the panel is operator-facing and the RPC is admin-gated); the
 * caller also gates on `skillsFeatureEnabled()` before mounting.
 *
 * `endpointId` is the wire-form base64 UUID (the same value the inventory row
 * carries), passed straight to ListEndpointSkillUsage.
 */
import { useEffect, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { listEndpointSkillUsage } from "../../lib/skillsClient.js";
import { isSurfaceDenied, useMyPermissions } from "../../lib/permissions.js";
import {
    OriginBadge,
    VendorBadge,
    descriptionPrefix,
    ellipsizePath,
    formatRelativeTime,
} from "../skills/skillBadges.jsx";

const PAGE_SIZE = 20;

const TH_CLASS =
    "text-left text-[9px] uppercase tracking-[0.08em] font-medium text-[color:var(--muted-soft)] px-[8px] py-[5px]";
const TD_CLASS =
    "px-[8px] py-[6px] align-top text-[11px] leading-[1.32] text-[color:var(--ink)] border-t border-[color:var(--line-soft)]";

export default function EndpointSkillUsagePanel({ endpointId, isAdmin }) {
    const caps = useMyPermissions();
    const denied = isSurfaceDenied(caps, "ListEndpointSkillUsage");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);

    useEffect(() => { setPage(0); }, [endpointId]);

    useEffect(() => {
        if (!isAdmin || !endpointId || denied) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        listEndpointSkillUsage({ endpointId, page, pageSize: PAGE_SIZE })
            .then((resp) => {
                if (cancelled) return;
                setRows(Array.isArray(resp?.rows) ? resp.rows : []);
                setTotal(Number(resp?.total ?? 0));
            })
            .catch((err) => { if (!cancelled) setError(err.message || String(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isAdmin, endpointId, page, denied]);

    if (!isAdmin || denied) return null;

    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

    return (
        <div className="grid gap-[8px]">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                Observed skills
            </span>

            {loading ? (
                <span className={`${NOTE_CLASS} text-[11px]`}>Loading…</span>
            ) : error ? (
                <span className="text-[11px] text-red-400" title={error}>Failed to load skills</span>
            ) : rows.length === 0 ? (
                <span className={`${NOTE_CLASS} text-[11px]`}>No skills observed on this endpoint.</span>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                <th className={TH_CLASS}>Name</th>
                                <th className={TH_CLASS}>Vendor</th>
                                <th className={TH_CLASS}>Origin</th>
                                <th className={TH_CLASS}>Disk path</th>
                                <th className={TH_CLASS}>Description</th>
                                <th className={TH_CLASS}>Last invoked</th>
                                <th className={`${TH_CLASS} text-right`}>Invocations</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const name = r.skillName ?? r.skill_name;
                                const diskPath = r.diskPath ?? r.disk_path ?? "";
                                const desc = r.description ?? "";
                                const preview = r.skillMdPreview ?? r.skill_md_preview ?? "";
                                const lastInvoked = r.lastInvokedAt ?? r.last_invoked_at;
                                const invocations = r.invocationCount ?? r.invocation_count ?? 0;
                                const sha = r.contentSha256 ?? r.content_sha256;
                                return (
                                    <tr key={`${name}|${sha}`}>
                                        <td className={`${TD_CLASS} font-mono`}>{name}</td>
                                        <td className={TD_CLASS}><VendorBadge vendor={r.vendor} /></td>
                                        <td className={TD_CLASS}>
                                            <OriginBadge origin={r.origin} pluginId={r.pluginId ?? r.plugin_id} />
                                        </td>
                                        <td className={`${TD_CLASS} font-mono text-[10px] text-[color:var(--muted)]`}>
                                            <span title={diskPath}>{ellipsizePath(diskPath)}</span>
                                        </td>
                                        <td className={`${TD_CLASS} text-[color:var(--muted)] max-w-[260px]`}>
                                            <span title={preview || desc}>{descriptionPrefix(desc, 80)}</span>
                                        </td>
                                        <td className={`${TD_CLASS} text-[color:var(--muted)] whitespace-nowrap`}>
                                            {formatRelativeTime(lastInvoked)}
                                        </td>
                                        <td className={`${TD_CLASS} text-right tabular-nums text-[color:var(--muted)]`}>
                                            {Number(invocations).toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {total > PAGE_SIZE ? (
                <div className="flex items-center justify-end gap-[8px] text-[11px] text-[color:var(--muted)]">
                    <span className={NOTE_CLASS}>Page {page + 1} of {lastPage + 1}</span>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0 || loading}
                        className="h-[26px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 cursor-pointer"
                    >
                        Prev
                    </button>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                        disabled={page >= lastPage || loading}
                        className="h-[26px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 cursor-pointer"
                    >
                        Next
                    </button>
                </div>
            ) : null}
        </div>
    );
}
